import axios from 'axios';
import { useRouter } from 'next/router';
import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { Image } from '@components/common/Image';
import PageLoading from '@components/common/page-loading/page-loading';
import { UiKitButton } from '@components/common/ui-kit-button';
import { currencyReplacer } from '@features/Payments';
import { ButtonGroup } from '@features/Unsubscribe/components/ButtonGroup';
import { Container } from '@features/Unsubscribe/components/Container';
import { GiftCard } from '@features/Unsubscribe/components/GiftCard';
import { Title } from '@features/Unsubscribe/components/Title';
import { useUnsubscribeAnalytics } from '@features/Unsubscribe/hooks/use-unsubscribe-analytics';
import type { TpWizardSteps } from '@features/Unsubscribe/types';
import { useHandleNext } from '@features/Unsubscribe/utils/go-to-next';
import { WIZARD_STEPS } from '../../сonstants';
import { useGetAppName } from 'src/utils/hooks';
import styles from './SpecialDiscount.module.scss';
export const SpecialDiscount = (props: TpWizardSteps) => {
  const {
    defaultEventOptions,
    trackUnsubscribeEvent,
    trackUnsubscribeRequestError,
    trackUnsubscribeRequestSuccess
  } = useUnsubscribeAnalytics();
  const {
    wizard,
    featureFlags,
    allUnsubscribeData
  } = props;
  const {
    query
  } = useRouter();
  const discountPlan = allUnsubscribeData?.cancellation_year_discount;
  const app = useGetAppName();
  const currency = currencyReplacer(discountPlan ? discountPlan.currency : 'USD');
  const currentUrlUpgrade = useMemo(() => {
    if (featureFlags?.paymentSystem === 'Stripe') {
      return 'stripe/modify_subscription';
    } else if (featureFlags?.paymentSystem === 'Braintree') {
      return '/braintree_upgrade';
    } else {
      return 'stripe/modify_subscription';
    }
  }, [featureFlags?.paymentSystem]);
  const handleNext = useHandleNext({
    query: query,
    wizard: wizard,
    url: currentUrlUpgrade,
    eventName: 'cancel_special_discount_chosen',
    options: {
      variant: '1-get discount'
    },
    successStep: WIZARD_STEPS.DISCOUNT_ACTIVATED,
    new_plan_id: discountPlan?.upgradeId,
    requestAnalytics: {
      successEventName: 'cancel_subscription_change_succeeded',
      errorEventName: 'cancel_subscription_change_failed',
      options: {
        endpoint: currentUrlUpgrade,
        paymentSystem: featureFlags?.paymentSystem,
        requestName: 'change_subscription'
      }
    }
  });
  const handleCancel = useCallback(async () => {
    const formData = {
      app: app,
      email: defaultEventOptions.email,
      user_id: defaultEventOptions.userId
    };
    const requestOptions = {
      endpoint: 'cancel_subscription',
      requestName: 'cancel_subscription',
      source: 'special_discount_cancel_anyway'
    };
    try {
      const response = await axios({
        method: 'post',
        url: `${process.env.NEXT_PUBLIC_API_MICROSERVICE_ENDPOINT}cancel_subscription`,
        data: qs.stringify(formData)
      });
      trackUnsubscribeRequestSuccess('cancel_subscription_cancel_succeeded', requestOptions, response.status);
      trackUnsubscribeEvent('cancel_special_discount_chosen', {
        variant: '2-cancel'
      });
      wizard.goToStep(WIZARD_STEPS.SAD);
    } catch (error) {
      trackUnsubscribeRequestError('cancel_subscription_cancel_failed', error, requestOptions);
      wizard.goToStep(WIZARD_STEPS.ERROR);
      console.log('error', error);
    }
  }, [defaultEventOptions.email, defaultEventOptions.userId, trackUnsubscribeEvent, trackUnsubscribeRequestError, trackUnsubscribeRequestSuccess, wizard, app]);
  return useMemo(() => discountPlan == null ? <PageLoading /> : <Container className={styles.container} eventOnMounted="cancel_special_discount_opened" eventOptions={defaultEventOptions}>
          <Title className={styles.title}>A special discount for you!</Title>
          <p className={styles.subtitle}>
            <em>Only</em>{' '}
            <s>
              {currency} {discountPlan.fullPrice}
            </s>{' '}
            {currency} {discountPlan.fullPriceDiscount} for 1 year
          </p>
          <Image src="special-80-discount.webp" alt="gift-premium" width={327} height={210} />
          <GiftCard title="80% off" subtitle="for 365 days of Premium" image="present-bow-green.webp" titleClassName={styles.giftCardTitle} />

          <ButtonGroup className={styles.buttonGroup}>
            <UiKitButton color="pure" onClick={handleCancel} className={styles.button}>
              Cancel anyway
            </UiKitButton>
            <UiKitButton fullWidth onClick={handleNext}>
              Get my discount
            </UiKitButton>
          </ButtonGroup>
        </Container>, [currency, defaultEventOptions, discountPlan, handleCancel, handleNext]);
};